import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { docSignAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { CheckCircle, PenTool, ShieldCheck } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FIELD_COLORS = {
  signature: '#7C3AED', initials: '#7C3AED', text: '#2563EB', name: '#059669',
  email: '#D97706', phone: '#DC2626', date: '#0891B2', checkbox: '#4F46E5',
  dropdown: '#9333EA', number: '#EA580C',
};

export default function DocSignPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({});
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const pageRefs = useRef({});

  useEffect(() => {
    (async () => {
      try {
        const res = await docSignAPI.get(token);
        setData(res.data);
        if (res.data.status === 'signed') { setSigned(true); return; }
        const init = {};
        const pre = res.data.prefilled_fields || {};
        (res.data.fields || []).forEach(f => {
          init[f.id] = pre[f.id] || (f.type === 'checkbox' ? false : '');
        });
        setFormData(init);
      } catch { setData({ error: true }); }
      setLoading(false);
    })();
  }, [token]);

  const handleSign = async () => {
    const fields = data?.fields || [];
    for (const f of fields) {
      if (f.required && f.type !== 'checkbox' && !formData[f.id]?.toString().trim()) {
        toast.error(`"${f.label}" is required`);
        return;
      }
    }
    const sigField = fields.find(f => f.type === 'signature');
    if (sigField && !formData[sigField.id]?.trim()) {
      toast.error('Please provide your signature');
      return;
    }
    setSigning(true);
    try {
      await docSignAPI.sign(token, {
        form_data: formData,
        signature: formData[fields.find(f => f.type === 'signature')?.id] || Object.values(formData).find(v => typeof v === 'string' && v.length > 0) || 'signed',
      });
      setSigned(true);
      toast.success('Document signed successfully!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Signing failed'); }
    setSigning(false);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-100"><div className="animate-spin w-8 h-8 border-2 border-[#E31937] border-t-transparent rounded-full" /></div>;
  if (data?.error) return <div className="min-h-screen flex items-center justify-center bg-gray-100"><p className="text-gray-500">Invalid or expired signing link.</p></div>;

  if (signed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <Card className="max-w-md w-full border-green-200 shadow-xl">
          <CardContent className="p-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Document Signed</h1>
            <p className="text-gray-500">Your document has been signed. A copy will be sent to your email.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const pages = data?.page_images || [];
  const fields = data?.fields || [];

  const renderField = (field, scaleX, scaleY) => {
    const color = FIELD_COLORS[field.type] || '#FBBF24';
    const style = {
      position: 'absolute',
      left: field.x * scaleX,
      top: field.y * scaleY,
      width: field.width * scaleX,
      height: field.height * scaleY,
    };

    if (field.type === 'signature' || field.type === 'initials') {
      return (
        <div key={field.id} style={style} data-testid={`sign-field-${field.id}`}>
          <Input
            value={formData[field.id] || ''}
            onChange={e => setFormData(p => ({ ...p, [field.id]: e.target.value }))}
            placeholder={field.type === 'signature' ? 'Type your signature...' : 'Type initials...'}
            className="w-full h-full text-base bg-white/90 border-2"
            style={{ borderColor: color, fontFamily: "'Brush Script MT', 'Segoe Script', cursive", fontSize: field.type === 'signature' ? '1.2rem' : '0.9rem' }}
          />
        </div>
      );
    }

    if (field.type === 'checkbox') {
      return (
        <div key={field.id} style={style} className="flex items-center justify-center" data-testid={`sign-field-${field.id}`}>
          <input
            type="checkbox"
            checked={!!formData[field.id]}
            onChange={e => setFormData(p => ({ ...p, [field.id]: e.target.checked }))}
            className="w-full h-full accent-[#E31937] cursor-pointer"
          />
        </div>
      );
    }

    if (field.type === 'dropdown') {
      return (
        <div key={field.id} style={style} data-testid={`sign-field-${field.id}`}>
          <Select value={formData[field.id] || ''} onValueChange={v => setFormData(p => ({ ...p, [field.id]: v }))}>
            <SelectTrigger className="w-full h-full text-xs bg-white/90 border-2" style={{ borderColor: color }}><SelectValue placeholder="Select..." /></SelectTrigger>
            <SelectContent>{(field.options || []).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      );
    }

    if (field.type === 'date') {
      return (
        <div key={field.id} style={style} data-testid={`sign-field-${field.id}`}>
          <Input
            type="date"
            value={formData[field.id] || ''}
            onChange={e => setFormData(p => ({ ...p, [field.id]: e.target.value }))}
            className="w-full h-full text-xs bg-white/90 border-2"
            style={{ borderColor: color }}
          />
        </div>
      );
    }

    return (
      <div key={field.id} style={style} data-testid={`sign-field-${field.id}`}>
        <Input
          type={field.type === 'number' ? 'number' : field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text'}
          value={formData[field.id] || ''}
          onChange={e => setFormData(p => ({ ...p, [field.id]: e.target.value }))}
          placeholder={field.label}
          className="w-full h-full text-xs bg-white/90 border-2"
          style={{ borderColor: color }}
        />
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-200 py-6 px-4" data-testid="doc-sign-page">
      {/* Header */}
      <div className="max-w-3xl mx-auto mb-4">
        <div className="bg-[#E31937] text-white text-center py-4 rounded-xl shadow-lg">
          <h1 className="text-xl font-bold">CANADIAN TRUCK SALES</h1>
          <p className="text-xs opacity-80 mt-0.5">{data?.template_name}</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto mb-4 bg-white rounded-lg shadow-md p-3 flex items-center justify-between">
        <div className="text-sm">
          <span className="text-gray-500">Signing as:</span> <strong>{data?.signer_name}</strong> ({data?.signer_email})
        </div>
        <Button className="bg-[#E31937] hover:bg-[#C4122C] text-white" onClick={handleSign} disabled={signing} data-testid="submit-signing-btn">
          <PenTool className="w-4 h-4 mr-1" /> {signing ? 'Signing...' : 'Finish & Sign'}
        </Button>
      </div>

      {/* Document Pages with Fields */}
      <div className="max-w-3xl mx-auto space-y-4">
        {pages.map(pg => (
          <div
            key={pg.page}
            ref={el => pageRefs.current[pg.page] = el}
            className="relative bg-white shadow-lg rounded"
            style={{ aspectRatio: `${pg.width} / ${pg.height}` }}
          >
            <img src={`${API_URL}${pg.url}`} alt={`Page ${pg.page}`} className="w-full h-full object-contain rounded" draggable={false} />
            {fields.filter(f => f.page === pg.page).map(field => {
              const rect = pageRefs.current[pg.page]?.getBoundingClientRect();
              if (!rect) return null;
              const scaleX = rect.width / pg.width;
              const scaleY = rect.height / pg.height;
              return renderField(field, scaleX, scaleY);
            })}
          </div>
        ))}
      </div>

      <div className="max-w-3xl mx-auto mt-4 text-center">
        <Button className="bg-[#E31937] hover:bg-[#C4122C] text-white h-12 px-8 text-base" onClick={handleSign} disabled={signing} data-testid="submit-signing-btn-bottom">
          <PenTool className="w-4 h-4 mr-1" /> {signing ? 'Signing...' : 'Finish & Sign Document'}
        </Button>
        <div className="flex items-center gap-2 text-[10px] text-gray-400 justify-center mt-3">
          <ShieldCheck className="w-3 h-3" />
          <span>Your IP address and timestamp are recorded for legal verification</span>
        </div>
      </div>
    </div>
  );
}
